import { useCallback } from "react";
import { cloneDeep } from "lodash";
import { useFormAPI } from "@/widgets/classic-form/form";
import { LOCATION_EXACTNESS_SCOPE } from "@/entities/place";
import { usePageContextQuery } from "@/shared/page-context";
import { useEventFieldsWithExactnessDisabled } from "../shared/useEventFieldsWithExactnessDisabled";

export const useToggleEventExactness = () => {
    const { setFieldValue, getFieldValue } = useFormAPI();
    const config = useEventFieldsWithExactnessDisabled();
    const { data: pageContext } = usePageContextQuery();
    const defaultYearProximity = pageContext.runtimeConfig.defaultYearProximity;

    return useCallback(
        (newValue: boolean) => {
            const eventValues = getFieldValue("events");
            const valuesCopy = cloneDeep(eventValues);
            Object.entries(valuesCopy).forEach(([eventKey, event]) => {
                const yearDisabled = config.yearDisabled.has(eventKey);
                const placeDisabled = config.placeDisabled.has(eventKey);

                event.instances.forEach((instance) => {
                    if (!newValue) {
                        // MATE uncheck: reset filled fields to Broad, empty fields to default proximity
                        if (!instance.isRangeMode && !yearDisabled) {
                            const hasYear = typeof instance.date.year === "number";
                            if (hasYear) {
                                instance.dateProximity.year = null;
                                instance.dateProximity.month = null;
                                instance.dateProximity.day = null;
                            } else {
                                instance.dateProximity.year = defaultYearProximity;
                                instance.dateProximity.month = defaultYearProximity !== null ? 0 : null;
                                instance.dateProximity.day = defaultYearProximity !== null ? 0 : null;
                            }
                        }
                        if (!placeDisabled) {
                            instance.locationExactness = null;
                        }
                        return;
                    }

                    if (!instance.isRangeMode && !yearDisabled) {
                        // MATE check: override Broad (null) to Exact always.
                        // Override experiment default (±10) to Exact only on empty-year fields —
                        // filled fields with the default proximity are treated as user-accepted.
                        const hasYear = typeof instance.date.year === "number";
                        const isOverridable =
                            instance.dateProximity.year === null ||
                            (!hasYear && instance.dateProximity.year === defaultYearProximity);
                        if (isOverridable) {
                            instance.dateProximity.year = 0;
                            instance.dateProximity.month = 0;
                            instance.dateProximity.day = 0;
                        }
                    }
                    if (!placeDisabled) {
                        // MATE check: only promote Broad (null) to ThisPlace.
                        // Any non-null value — including the computed level-based default
                        // (e.g. "exact to State") — is preserved unchanged (EGE-8089 AC 6.1).
                        if (!instance.locationExactness) {
                            instance.locationExactness = LOCATION_EXACTNESS_SCOPE.ThisPlace;
                        }
                    }
                });
            });
            setFieldValue("events", valuesCopy);
        },
        [getFieldValue, setFieldValue, config, defaultYearProximity],
    );
};
